import { useEffect, useMemo, useState } from 'react';

export type TextChunk = {
  value: string;
  bold?: boolean;
  underline?: boolean;
};

type TypewriterTextProps = {
  chunks: TextChunk[];
  speedMs?: number;
};

const TypewriterText = ({ chunks, speedMs = 10 }: TypewriterTextProps) => {
  const [visibleLength, setVisibleLength] = useState(0);

  const totalLength = useMemo(
    () => chunks.reduce((accumulator, chunk) => accumulator + chunk.value.length, 0),
    [chunks],
  );

  const visibleChunks = useMemo(() => {
    let remainingLength = visibleLength;

    return chunks
      .map(chunk => {
        if (remainingLength <= 0) {
          return null;
        }

        const visibleValue = chunk.value.slice(0, remainingLength);
        remainingLength -= visibleValue.length;

        if (!visibleValue.length) {
          return null;
        }

        return {
          ...chunk,
          value: visibleValue,
        };
      })
      .filter((chunk): chunk is TextChunk => chunk !== null);
  }, [chunks, visibleLength]);

  useEffect(() => {
    setVisibleLength(0);

    if (!totalLength) {
      return;
    }

    const intervalId = window.setInterval(() => {
      setVisibleLength(previousLength => {
        if (previousLength >= totalLength) {
          window.clearInterval(intervalId);
          return previousLength;
        }

        return previousLength + 1;
      });
    }, speedMs);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [totalLength, speedMs]);

  return (
    <span aria-live="polite">
      {visibleChunks.map((chunk, index) => (
        <span
          key={`${index}-${chunk.value.length}`}
          className={`${chunk.bold ? 'fw-bold' : ''} ${chunk.underline ? 'text-decoration-underline' : ''}`.trim()}
        >
          {chunk.value}
        </span>
      ))}
    </span>
  );
};

export default TypewriterText;
